<template>
  <header-page-layout page-title="Campaigns">
    <template slot="actions">
      <b-button :to="{ name: 'campaigns-create' }" variant="primary">Create a Campaign</b-button>
    </template>

    <b-form inline class="d-block w-100 mb-4" @submit.prevent>
      <b-input-group>
        <b-form-input
          type="search"
          placeholder="Type here to find campaigns by name or category"
          autofocus
          v-model="filterQuery"
        ></b-form-input>
      </b-input-group>

      <b-alert v-if="filterQuery" variant="info" show class="my-2">
        Displaying {{ filteredCampaigns.length }} of {{ campaigns.length }} campaigns

        <b-button variant="outline-secondary" size="sm" @click="clearFilter">Show All</b-button>
      </b-alert>
    </b-form>

    <b-row v-for="category in campaignCategories" class="mb-4" :key="category.name">
      <b-col>
        <b-row class="mb-2">
          <b-col lg="8">
            <h3>{{ category.name }}</h3>
          </b-col>

          <b-col lg="4" class="text-lg-right text-sm-nowrap mt-lg-0">
            <b-button
              v-if="category.name !== 'Uncategorized'"
              :to="{ name: 'campaigns-create', query: { category_name: category.name } }"
              variant="outline-primary"
              size="sm"
            >Create Campaign</b-button>
          </b-col>
        </b-row>

        <b-table
          fixed
          stacked="md"
          :items="category.campaigns"
          :fields="fields"
          class="bg-white"
        >
          <template slot="name" slot-scope="data">
            <router-link :to="goTo(data)">
              {{data.item.name && data.item.name.trim().length > 0 ? data.item.name : '[unnamed]'}}
            </router-link>
          </template>

          <template slot="automated" slot-scope="data">
            <fa icon="check-circle" v-if="data.item.active"></fa>
          </template>

          <template slot="sent" slot-scope="data">
            {{data.item.sent}}
          </template>

          <template slot="opened" slot-scope="data">
            {{getPercent(data.item, 'opened')|percentage}}% ({{data.item.opened}})
          </template>

          <template slot="clicks" slot-scope="data">
            {{getPercent(data.item, 'clicked')|percentage}}% ({{data.item.clicked}})
          </template>
        </b-table>

      </b-col>
    </b-row>

    <b-row v-if="campaigns.length === 0">
      <b-col>
        <b-alert variant="info" show>No campaigns have been created yet</b-alert>
      </b-col>
    </b-row>
  </header-page-layout>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import _ from 'lodash'
import HeaderPageLayout from '../components/HeaderPageLayout'
import store from '../store'

export default {
  components: {
    HeaderPageLayout
  },

  data() {
    return {
      fields: [
        { key: 'name', thClass: ['w-50'] },
        { key: 'automated', tdClass: ['text-nowrap'] },
        { key: 'sent', tdClass: ['text-nowrap'] },
        { key: 'opened', tdClass: ['text-nowrap'] },
        { key: 'clicks', tdClass: ['text-nowrap'] }
      ],

      filterQuery: ''
    }
  },

  beforeRouteEnter(to, from, next) {
    Promise.all([
      store.dispatch('campaigns/fetchAll')
    ])
      .then(() => next())
  },

  computed: {
    ...mapState('campaigns', [
      'campaigns'
    ]),
    getPercent() {
      return (data, type) => {
        if (data[type]) {
          return (data[type] / data['sent']) * 100
        }
        return 0
      }
    },
    normalizedCampaigns() {
      // intentionally rename any null categories to 'Uncategorized' so they can be searched
      return this.campaigns.map(c => c.category_name === null ? { ...c, category_name: 'Uncategorized' } : c)
    },

    filteredCampaigns() {
      return this.filterQuery ? this.normalizedCampaigns.filter(c => {
        const lowerQuery = this.filterQuery.toLowerCase()

        return (c.name && c.name.toLowerCase().includes(lowerQuery)) ||
        (c.category_name && c.category_name.toLowerCase().includes(lowerQuery))
      }) : this.normalizedCampaigns
    },

    campaignCategories() {
      return _(this.filteredCampaigns)
        .sortBy(['name']) // pre-sort so campaigns will automatically sorted after grouping
        .groupBy(c => c.category_name)
        .map((campaigns, category) => {
          return {
            name: category,
            campaigns
          }
        })
        .sortBy((category) => category.name === 'Uncategorized' ? null : category.name) // force 'Uncategorized' last
        .value()
    }
  },

  methods: {
    ...mapActions('campaigns', [
      'fetchAll'
    ]),
    goTo(data) {
      return { name: 'campaigns-edit', params: { id: data.item._id } }
    },
    clearFilter() {
      this.filterQuery = ''
    }
  },

  filters: {
    percentage(val) {
      return val ? val.toFixed(1) : 0
    }
  }
}
</script>

<style scoped>
h4 .edit {
  font-size: 75%;
}
</style>
